import React, { useContext, useEffect, useState } from 'react'
import { PostTenant } from '../../../Interfaces/Types'
import Modal from '@embracesbs/component-modal'
import TextFieldWrapper from '@embracesbs/component-textfieldwrapper/TextFieldWrapper'
import TextField from '@embracesbs/component-textfield/TextField'
import Container from '@embracesbs/component-container/Container'
import Text from '@embracesbs/component-text'
import { useConfig } from '../../Config/ConfigContext'
import Autocomplete from '@embracesbs/component-autocomplete'
import { useSubscriptions } from '../../Context/SubscriptionContext'
import { useApi } from '../../../Hooks'
import { useSystemStatus } from '../../Context/SystemStatusContext'
import { AuthRoleContext } from '../../Auth/AuthRoleContext'

interface AddTenantProps {
  isOpen: boolean
  onClosed: () => void
  onCreated: (tenant?: any) => void
}

interface EnviromentOptions {
  label: string
  value: string
}

// Mirrors the backend rule (TenantController): start with a letter, 3-17 chars,
// lowercase letters / numbers / hyphens only — no spaces, underscores or uppercase.
const TENANT_NAME_REGEX = /^[a-z][a-z0-9-]{2,16}$/
const TENANT_NAME_HINT = 'Start with a letter, 3–17 characters, lowercase letters, numbers and hyphens only (no spaces or underscores).'

const AddTenant: React.FC<AddTenantProps> = ({ isOpen, onClosed, onCreated }) => {
  const config = useConfig()
  const { fetchApi } = useApi()
  const [tenantName, setTenantName] = useState<string>('')
  const [tenantDisplayName, setTenantDisplayName] = useState<string>('')
  const [environment, setEnvironment] = useState<string>('')
  const [tenantNameError, setTenantNameError] = useState<string>('')
  const [displayNameError, setDisplayNameError] = useState<string>('')
  const [environmentError, setEnvironmentError] = useState<string>('')
  const { selectedSubscription } = useSubscriptions()

  const [environmentOptions, setEnvironmentOptions] = useState<EnviromentOptions[]>([])

  useEffect(() => {
    if (config && config.environments) {
      const options: EnviromentOptions[] = config.environments.map((environment) => ({
        label: environment,
        value: environment
      }))
      setEnvironmentOptions(options)
    }
  }, [config])

  const { maintenance } = useSystemStatus()
  const roles = useContext(AuthRoleContext)
  const isMaintenanceBlocked = maintenance.enabled && !roles?.isAdmin

  // All three are required to create a tenant — used to gate the Save button.
  const isValid = TENANT_NAME_REGEX.test(tenantName) && tenantDisplayName.trim() !== '' && environment !== '' && !isMaintenanceBlocked

  const handleAddTenant = async () => {
    let valid = true
    if (tenantName.trim() === '') {
      setTenantNameError('Tenant name is required')
      valid = false
    } else if (!TENANT_NAME_REGEX.test(tenantName)) {
      setTenantNameError(TENANT_NAME_HINT)
      valid = false
    }
    if (tenantDisplayName.trim() === '') {
      setDisplayNameError('Display name is required')
      valid = false
    }
    if (environment === '') {
      setEnvironmentError('Environment is required')
      valid = false
    }
    if (!valid) return

    const tenant: PostTenant = {
      tenantDisplayname: tenantDisplayName,
      tenantName: tenantName,
      environment: environment
    }

    try {
      const data = await fetchApi({
        endpoint: `/api/subscription/${selectedSubscription.id}/tenant`,
        method: 'POST',
        body: tenant
      })
      onCreated(data)
      onClosed()
    } catch (error: any) {
      if (error.response && error.response.status === 400 && error.response.data) {
        setTenantNameError(typeof error.response.data === 'string' ? error.response.data : 'Invalid tenant name.')
      } else {
        setTenantNameError('An unexpected error occurred.')
      }
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      width='50%'
      onClose={() => onClosed()}
      hasHeaderBorder={true}
      headerTitle={`Add Tenant in ${selectedSubscription?.customerName}`}
      height={'60%'}
      onConfirm={handleAddTenant}
      isConfirmDisabled={!isValid}
      variant='prompt'
      labelConfirm='Save'
    >
      <Container isVertical>
        <TextFieldWrapper labelText='Tenant Name' isRequired>
          <TextField
            placeholder='Tenant Name'
            value={tenantName}
            onChange={(e) => {
              const value = e.target.value
              setTenantName(value)
              setTenantNameError(value === '' || TENANT_NAME_REGEX.test(value) ? '' : TENANT_NAME_HINT)
            }}
            hasAnError={tenantNameError !== ''}
            errorMessage={tenantNameError}
            onClear={() => {
              setTenantName('')
              setTenantNameError('')
            }}
            maxLength={17}
          />
        </TextFieldWrapper>
        <Text textStyle='description' textColor='grey' content={TENANT_NAME_HINT} style={{ marginTop: '-6px', marginBottom: '6px' }} />

        <TextFieldWrapper labelText='Tenant Display Name' isRequired>
          <TextField
            placeholder='Tenant Display Name'
            value={tenantDisplayName}
            onChange={(e) => {
              setTenantDisplayName(e.target.value)
              setDisplayNameError('')
            }}
            hasAnError={displayNameError !== ''}
            errorMessage={displayNameError}
          />
        </TextFieldWrapper>

        <TextFieldWrapper labelText='Environment' isRequired>
          <Autocomplete
            placeholder='Select an environment...'
            onChange={(e) => {
              setEnvironment(e.value)
              setEnvironmentError('')
            }}
            options={environmentOptions}
            {...(environmentError !== '' ? { hasAnError: true, errorMessage: environmentError, errorMessageColor: 'danger' as const } : {})}
          />
        </TextFieldWrapper>
      </Container>
    </Modal>
  )
}

export default AddTenant
